import React from 'react';

import CountryDropdown from '@/components/common-components/CountryDropdown';

import { getLocationDetails } from '../../../../services/util.service';
import { parseAddress } from '../../../../utils/address-detection-utils';
import PhoneInput from '../../../common-components/PhoneInput/PhoneInput';

export default function AddressForm({
  address,
  setAddress,
  validationErrors = {},
  showFullNameField = false,
  influencerFullName,
  setInfluencerFullName,
  showPhone = true,
  countryDropdownClassName = '',
}) {
  /**
   * function to fetch address details from pincode
   */
  const handleFetchLocation = async (zipcode) => {
    const data = await getLocationDetails(zipcode);
    delete data?.success;

    if (data) {
      if (data?.city === '' && data?.country === '' && data?.state === '') {
        validationErrors.pincodeWarn = 'That code may be wrong';
      }
    }

    setAddress((prev) => ({ ...prev, ...data }));
  };

  /**
   * function to handle input change in address fields
   */
  const handleInputChange = async (e, name) => {
    const { value } = e.target;
    if (name === 'pincode' && value !== '') {
      const trimmedValue = value.trim();
      if (trimmedValue.length <= 8) {
        setAddress((prev) => ({ ...prev, [name]: trimmedValue }));
        if (trimmedValue.length >= 5) {
          handleFetchLocation(trimmedValue);
        } else {
          validationErrors.pincodeWarn = '';
        }
      }
    } else {
      setAddress({ ...address, [name]: value });
    }
  };

  const handleAddressPaste = async (e, handler) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData('text');
    const parsedAddress = await parseAddress(pastedText);

    if (parsedAddress) {
      setAddress((prevAddress) => ({
        ...prevAddress,
        addressLineOne: parsedAddress.addressLine1,
        addressLineTwo: parsedAddress.addressLine2 || '',
        city: parsedAddress.city || '',
        state: parsedAddress.state || '',
        country: parsedAddress.country,
        pincode: parsedAddress.pincode,
      }));

      if (parsedAddress.name && setInfluencerFullName) {
        setInfluencerFullName(parsedAddress.name);
      }
    } else {
      handler(e);
    }
  };

  return (
    <div className="address-form">
      {showFullNameField && (
        <div className="text-input-container">
          <div className="text-field-wrapper">
            <label className="required-field">Full Name</label>
            <input
              type="text"
              placeholder={`Influencer's Full Name`}
              value={influencerFullName}
              onChange={(e) => setInfluencerFullName(e.target.value)}
              onPaste={(e) =>
                handleAddressPaste(e, (event) => setInfluencerFullName(event.clipboardData.getData('text')))
              }
              className="text-base"
            />
            {validationErrors.fullName && <div className="errorMessage">{validationErrors.fullName}</div>}
          </div>
        </div>
      )}
      <div className="text-input-container">
        <div className="text-field-wrapper">
          <label className="required-field">Address</label>
          <input
            type="text"
            placeholder={`e.g. Empire State Building`}
            value={address.addressLineOne}
            onChange={(e) => handleInputChange(e, 'addressLineOne')}
            onPaste={(e) =>
              handleAddressPaste(e, (event) =>
                handleInputChange({ target: { value: event.clipboardData.getData('text') } }, 'addressLineOne')
              )
            }
            className="text-base"
            maxLength={35}
          />
          {validationErrors.addressLineOne ? (
            <div className="errorMessage">{validationErrors.addressLineOne}</div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-subdued)', marginTop: 4 }}>
              Must be between 4 and 35 characters
            </div>
          )}
        </div>
      </div>
      <div className="text-input-container">
        <div className="text-field-wrapper">
          <label>Apartment, suite, etc</label>
          <div className="text-field-inner-wrapper">
            <input
              type="text"
              placeholder="e.g. 10th floor"
              value={address.addressLineTwo}
              onChange={(e) => handleInputChange(e, 'addressLineTwo')}
              className="text-base"
            />
            {validationErrors.addressLineTwo && <div className="errorMessage">{validationErrors.addressLineTwo}</div>}
          </div>
        </div>
        <div className="text-field-wrapper">
          <label className="required-field">Postal Code</label>
          <div className="text-field-inner-wrapper">
            <input
              type="text"
              placeholder="e.g. 10001"
              value={address.pincode}
              onChange={(e) => handleInputChange(e, 'pincode')}
              className="text-base"
            />
          </div>
          {validationErrors.pincode && <div className="errorMessage">{validationErrors.pincode}</div>}
          {validationErrors.pincodeWarn && (
            <div className="errorMessage" style={{ color: 'var(--yellow)' }}>
              {validationErrors.pincodeWarn}
            </div>
          )}
        </div>
      </div>
      <div className="text-input-container">
        <div className="text-field-wrapper">
          <label className="required-field">City</label>
          <input
            type="text"
            placeholder="e.g. New York"
            value={address.city}
            onChange={(e) => handleInputChange(e, 'city')}
            className="text-base"
          />
          {validationErrors.city && <div className="errorMessage">{validationErrors.city}</div>}
        </div>
      </div>
      <div className="text-input-container">
        <div className="text-field-wrapper">
          <label>State</label>
          <input
            type="text"
            placeholder="e.g. New York"
            value={address.state}
            onChange={(e) => handleInputChange(e, 'state')}
            className="text-base"
          />
          {validationErrors.state && <div className="errorMessage">{validationErrors.state}</div>}
        </div>
      </div>
      {showPhone && (
        <PhoneInput
          label="Phone (Optional)"
          value={address.phone}
          onChange={(v, data, e, f) => setAddress((add) => ({ ...add, phone: v }))}
        />
      )}
      <div className={`text-input-container ${countryDropdownClassName}`}>
        <div className="text-field-wrapper w-full">
          <CountryDropdown
            value={address.country}
            handleChange={(v) => {
              setAddress((add) => ({
                ...add,
                country: v,
              }));
            }}
          />
          {validationErrors.country && <div className="errorMessage">{validationErrors.country}</div>}
        </div>
      </div>
    </div>
  );
}
